import { useEffect, useRef } from "react";
import { useTheme } from "@codelittinc/carbon-design-system";
import type { SaveThemeRequest, Theme } from "@carbon-os/server/contract";
import { trackApp } from "./analytics";
import { sessionFetch } from "./session";

// One save in flight for the whole page, and at most one waiting: clicks made
// while a save is out collapse into the latest, which goes next. Saves arrive in
// order, and the last choice is the one that stays saved.
let inFlight: Promise<void> | null = null;
let pending: Theme | null = null;

/**
 * Saves the theme on the employee's Gatekeeper profile. The screen has already
 * switched, so a failure is never shown: the server logs it and the next click
 * tries again.
 */
export function saveTheme(theme: Theme, fetchImpl: typeof fetch = sessionFetch): Promise<void> {
  pending = theme;
  inFlight ??= drain(fetchImpl);
  return inFlight;
}

async function drain(fetchImpl: typeof fetch): Promise<void> {
  while (pending !== null) {
    const body: SaveThemeRequest = { theme: pending };
    pending = null;
    await fetchImpl("/api/theme", {
      method: "PUT",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).catch(() => undefined);
  }
  // In the same turn as the last check, so a click right now starts a new drain.
  inFlight = null;
}

/**
 * Applies the saved theme once, when the page's data first arrives, then saves
 * every change the person makes after that. `saved` is undefined until then.
 * Applying the saved theme is not a change, so it is never written back.
 */
export function ThemeSync({ saved }: { saved: Theme | null | undefined }): null {
  const { theme, setTheme } = useTheme();
  // Refs, so StrictMode's second effect run neither applies nor saves twice.
  const armed = useRef(false);
  const baseline = useRef<Theme | null>(null);

  useEffect(() => {
    if (armed.current || saved === undefined) return;
    armed.current = true;
    baseline.current = saved ?? theme;
    if (saved !== null && saved !== theme) setTheme(saved);
  }, [saved, theme, setTheme]);

  useEffect(() => {
    if (!armed.current || theme === baseline.current) return;
    baseline.current = theme;
    void saveTheme(theme);
    trackApp("theme_changed", { theme });
  }, [theme]);

  return null;
}
